// hitTest (IMPLEMENTATION §2.7 "IDLE --down--> hitTest"): what is under a client point, read from the sim
// through query.cellView. The board shows the expansion mask; the rock around it (the rest of the fixed 7×9
// world) is drawn too, so a point outside the mask but inside the world grid is a rock cell (Expansion
// card); anything further out is `none`. Also: what can be dragged in which mode (§2.7 "Draggable by mode").
import { type Cell, clientToWorldCell, type Layout } from '../layout/computeLayout.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { FamId } from '../sim/gen/ids.ts';
import { cellView } from '../sim/query.ts';
import type { Building, SimState } from '../sim/state.ts';
import type { Mode } from './modes.ts';

export type HitObj =
  | { k: 'none' }
  | { k: 'rock'; x: number; y: number }
  | { k: 'empty'; x: number; y: number }
  | { k: 'item'; x: number; y: number; id: number; fam: FamId; tier: number }
  | { k: 'building'; x: number; y: number; id: number; b: Building };

/** The board cell (inside the shown mask) of a hit, or null. */
export function hitCell(h: HitObj): Cell | null {
  return h.k === 'none' || h.k === 'rock' ? null : { x: h.x, y: h.y };
}

export function hitTest(s: SimState, d: SimDefs, layout: Layout, cx: number, cy: number): HitObj {
  const c = clientToWorldCell(layout, cx, cy, d.board.cols, d.board.rows);
  if (c === null) return { k: 'none' };
  const v = cellView(s, d, c.x, c.y);
  switch (v.k) {
    case 'rock':
      return { k: 'rock', x: c.x, y: c.y };
    case 'empty':
      return { k: 'empty', x: c.x, y: c.y };
    case 'item':
      return { k: 'item', x: c.x, y: c.y, id: v.item.id, fam: v.item.fam, tier: v.item.tier };
    case 'building':
      return { k: 'building', x: c.x, y: c.y, id: v.b.id, b: v.b };
  }
}

/** Identity of the object for "same object" checks (double tap, rage tap). */
export function objKey(h: HitObj): string {
  switch (h.k) {
    case 'none':
      return 'none';
    case 'item':
      return `item:${h.id}`;
    case 'building':
      return `bld:${h.id}`;
    default:
      return `${h.k}:${h.x},${h.y}`;
  }
}

/**
 * §2.7 "Draggable by mode": Play (and the cold open) — loose items; Build — also machines, drills, deposits,
 * Crates, the Gallery and ghosts. Chutes are painted / erased, never dragged (`wired`).
 */
export function isDraggable(h: HitObj, mode: Mode): boolean {
  if (h.k === 'item') return true;
  if (h.k === 'building') return mode === 'build' && h.b.type !== 'chute';
  return false;
}

/** Long-press (Info) applies to items and buildings — never to a deposit in Play (it is mined). */
export function hasLongPress(h: HitObj, mode: Mode): boolean {
  if (h.k === 'item') return true;
  if (h.k === 'building') return !(h.b.type === 'deposit' && mode !== 'build');
  return false;
}
